<script lang="ts">
  /**
   * インライン編集フィールド。
   * クリック → 編集 → blur / Enter で commit。Esc で cancel。
   *
   * src/components/crm/InlineField.svelte の見た目を移植したが、
   * 本プリミティブは API 呼び出しを含まず、 `onSave` callback で
   * 親に保存処理を委譲する (rebuild の責務分離方針)。
   *
   * 既存利用箇所 (FirmSummary / LawyerDetail / DataTable) のため
   * 既存 props (`label`, `value`, `type`, `locked`, `disabled`,
   * `placeholder`, `onSave`) は維持。
   */
  interface SelectOption {
    value: string;
    label: string;
  }

  interface Props {
    value: string | number | null;
    /** 編集確定時に呼ばれる callback。空文字は cancellation として扱われる。 */
    onSave: (next: string) => void | Promise<void>;
    locked?: boolean;
    disabled?: boolean;
    placeholder?: string;
    type?: 'text' | 'number' | 'date' | 'select' | 'textarea';
    /** `type === 'select'` の場合の選択肢。 表示は label、 保存は value。 */
    options?: SelectOption[];
    /** 縦並びラベル (Summary レイアウト用)。 未指定なら inline-only。 */
    label?: string;
    /** `type === 'textarea'` の行数 (rows 属性)。 既定 4 行。 */
    textareaRows?: number;
    /** text / textarea の入力文字数上限。 */
    maxlength?: number;
    /**
     * `type === 'select'` で「(未設定)」 を選べるか。 既定 true (従来挙動)。
     * false にすると空値オプションを出さず、 空文字での保存 (値の解除) も行わない。
     * backend が `null` での解除に未対応な項目 (例: COALESCE 更新の position) 向け。
     */
    allowEmpty?: boolean;
    /** Text/textarea empty commits are a no-op unless this is explicitly enabled. */
    commitEmpty?: boolean;
    /**
     * クリック前に項目の種類が分かる opt-in 表示。 既定 'default' は従来挙動
     * (空値は `-`、 select でも chevron なし)。 既存呼び出し側は未指定で不変。
     * - 'text': 空値を `-` ではなく placeholder で薄く表示し、 常時うっすら入力枠を出す。
     * - 'select': 値 or `未選択` + chevron。 large select (候補は常時展開しない)。
     * - 'select-compact': 候補を segmented control として常時表示し、 選択で即 commit。
     * `disabled` / `locked` のときは affordance を出さず plain な readonly 表示に倒す
     * (編集可能に見える hover / focus / chevron / クリック可能 chip を出さない)。
     */
    affordance?: 'default' | 'text' | 'select' | 'select-compact';
    /** affordance='select' で空値のとき表示するラベル。 既定 `未選択`。 */
    emptyLabel?: string;
  }

  let {
    value,
    onSave,
    locked = false,
    disabled = false,
    placeholder = '',
    type = 'text',
    options = [],
    label,
    textareaRows = 4,
    maxlength,
    allowEmpty = true,
    commitEmpty = false,
    affordance = 'default',
    emptyLabel = '未選択',
  }: Props = $props();

  function toDisplay(v: string | number | null): string {
    if (v === null || v === undefined) return '';
    return String(v);
  }

  /** select 型のとき、 raw value を option label で解決する。 マッチしなければ raw を返す。 */
  function displayFor(v: string | number | null): string {
    const raw = toDisplay(v);
    if (type !== 'select') return raw;
    const opt = options.find((o) => o.value === raw);
    return opt?.label ?? raw;
  }

  let working = $state<string>(toDisplay(value));
  let editing = $state(false);
  let saving = $state(false);
  let savedAt = $state<number | null>(null);
  let error = $state<string | null>(null);

  let inputEl: HTMLInputElement | HTMLTextAreaElement | null = $state(null);

  $effect(() => {
    if (!editing) working = toDisplay(value);
  });

  const canEdit = $derived(!locked && !disabled);

  function startEdit(): void {
    if (!canEdit || saving) return;
    editing = true;
    queueMicrotask(() => {
      inputEl?.focus();
      inputEl?.select?.();
    });
  }

  function cancelEdit(): void {
    working = toDisplay(value);
    editing = false;
    error = null;
  }

  /// 値 `next` を保存する共通処理。 編集フロー (commit) と
  /// compact segmented control (commitValue 直呼び) の両方から使う。
  /// empty / no-op ガードと PATCH semantics は従来 commit と同一。
  async function commitValue(next: string): Promise<void> {
    if (!canEdit || saving) return;
    const current = toDisplay(value);
    if (next === current) {
      editing = false;
      return;
    }
    if (next.length === 0 && type !== 'select' && !commitEmpty) {
      // Text-style empty commits are usually "leave unchanged"; callers that
      // need explicit null clearing opt in with commitEmpty.
      working = current;
      editing = false;
      return;
    }
    if (next.length === 0 && type === 'select' && !allowEmpty) {
      working = current;
      editing = false;
      return;
    }
    saving = true;
    error = null;
    try {
      await onSave(next);
      savedAt = Date.now();
      setTimeout(() => {
        if (savedAt && Date.now() - savedAt >= 1900) savedAt = null;
      }, 2000);
      editing = false;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
      working = current;
    } finally {
      saving = false;
    }
  }

  async function commit(): Promise<void> {
    await commitValue(working);
  }

  const isEmptyValue = $derived(
    value === null || value === undefined || value === '',
  );

  function onKey(ev: KeyboardEvent): void {
    if (ev.isComposing || ev.keyCode === 229) return;
    if (ev.key === 'Enter') {
      ev.preventDefault();
      (ev.currentTarget as HTMLInputElement).blur();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      cancelEdit();
      (ev.currentTarget as HTMLInputElement).blur();
    }
  }

  /// textarea 用: Enter は改行 (default)、 Cmd/Ctrl+Enter で commit、 Esc は cancel。
  function onKeyTextarea(ev: KeyboardEvent): void {
    if (ev.isComposing || ev.keyCode === 229) return;
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
      ev.preventDefault();
      (ev.currentTarget as HTMLTextAreaElement).blur();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      cancelEdit();
      (ev.currentTarget as HTMLTextAreaElement).blur();
    }
  }
</script>

<div class="inline-field" class:saving class:locked class:disabled>
  {#if label}<span class="field-label">{label}</span>{/if}
  <span
    class="inline-field-control"
    title={locked ? '項目定義でロックされています' : undefined}
  >
    {#if !canEdit}
      {#if value === null || value === undefined || value === ''}
        <span class="muted">-</span>
      {:else}
        <span>{displayFor(value)}</span>
      {/if}
      {#if locked}<span class="lock-mark" aria-label="ロック">🔒</span>{/if}
    {:else if editing}
      {#if type === 'select'}
        <select
          bind:value={working}
          class="inline-control"
          disabled={saving}
          onblur={commit}
          onkeydown={onKey}
        >
          {#if allowEmpty}
            <option value="">(未設定)</option>
          {:else if !options.some((o) => o.value === working)}
            <!-- allowEmpty=false で現在値が選択肢に無い (未設定/不明) 時だけ、
                 選択できない placeholder を出す。 値の解除には使えない。 -->
            <option value="" disabled>選択してください</option>
          {/if}
          {#each options as opt (opt.value)}
            <option value={opt.value}>{opt.label}</option>
          {/each}
        </select>
      {:else if type === 'textarea'}
        <textarea
          bind:this={inputEl}
          bind:value={working}
          class="inline-control inline-control-textarea"
          rows={textareaRows}
          {maxlength}
          {placeholder}
          disabled={saving}
          onblur={commit}
          onkeydown={onKeyTextarea}
        ></textarea>
      {:else}
        <input
          bind:this={inputEl}
          bind:value={working}
          class="inline-control"
          {type}
          {placeholder}
          {maxlength}
          disabled={saving}
          onblur={commit}
          onkeydown={onKey}
        />
      {/if}
    {:else if affordance === 'select-compact' && type === 'select'}
      <!-- compact single select: 候補を segmented control として常時表示し、
           クリック前に選択肢が分かるようにする。 選択で即 commit。
           allowEmpty のときは通常 select の `(未設定)` 経路と同じく、 `未設定` chip
           から空値へ戻せるようにする (commitValue('') → caller が解除)。 -->
      <div class="compact-select" role="radiogroup">
        {#if allowEmpty}
          <button
            type="button"
            role="radio"
            aria-checked={isEmptyValue}
            class="compact-option compact-option-empty"
            class:selected={isEmptyValue}
            disabled={saving}
            onclick={() => commitValue('')}
          >
            未設定
          </button>
        {/if}
        {#each options as opt (opt.value)}
          <button
            type="button"
            role="radio"
            aria-checked={toDisplay(value) === opt.value}
            class="compact-option"
            class:selected={toDisplay(value) === opt.value}
            disabled={saving}
            onclick={() => commitValue(opt.value)}
          >
            {opt.label}
          </button>
        {/each}
      </div>
    {:else}
      <button
        type="button"
        class="inline-display"
        class:affordance-text={affordance === 'text'}
        class:affordance-select={affordance === 'select'}
        title="クリックして編集"
        onclick={startEdit}
      >
        {#if isEmptyValue}
          {#if affordance === 'text' && placeholder}
            <span class="placeholder-text">{placeholder}</span>
          {:else if affordance === 'select'}
            <span class="muted">{emptyLabel}</span>
          {:else}
            <span class="muted">-</span>
          {/if}
        {:else}
          <span class="display-value">{displayFor(value)}</span>
        {/if}
        {#if affordance === 'select'}
          <span class="select-chevron" aria-hidden="true">▾</span>
        {/if}
      </button>
    {/if}
    {#if saving}
      <span class="status-note small muted">保存中…</span>
    {:else if savedAt}
      <span class="status-note small saved">保存しました</span>
    {/if}
    {#if error}
      <span class="status-note small error-msg">{error}</span>
    {/if}
  </span>
</div>

<style>
  .inline-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .field-label {
    font-size: 0.75rem;
    color: var(--muted, #6b7280);
  }
  .inline-field-control {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
    min-height: 24px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .inline-display {
    display: inline-block;
    text-align: left;
    background: transparent;
    border: 1px dashed transparent;
    padding: 2px 6px;
    margin: -2px -6px;
    border-radius: 4px;
    cursor: text;
    font: inherit;
    color: inherit;
    width: 100%;
    min-width: 0;
    min-height: 24px;
    overflow-wrap: anywhere;
    word-break: break-word;
    /* textarea 由来の改行入り値も 1 ボタン内で複数行表示できるよう pre-wrap。
       text/select の値は通常改行を含まないので影響しない。 */
    white-space: pre-wrap;
  }
  .inline-display:hover,
  .inline-display:focus-visible {
    border-color: var(--border, #e3e6eb);
    background: #fafbfd;
    outline: none;
  }
  /* opt-in: text-like は常時うっすら入力枠を出して編集可能と分かるようにする。 */
  .inline-display.affordance-text {
    border-color: var(--border, #e3e6eb);
  }
  .placeholder-text {
    color: var(--muted, #9ca3af);
  }
  /* opt-in: select large は値 / 未選択 + chevron で「選択式」と分かるようにする。
     候補は常時展開せず、 クリックで既存 dropdown を開く。 */
  .inline-display.affordance-select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    border-color: var(--border, #e3e6eb);
    cursor: pointer;
  }
  .inline-display.affordance-select .display-value {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .select-chevron {
    flex: 0 0 auto;
    color: var(--muted, #6b7280);
    font-size: 0.7rem;
    line-height: 1;
  }
  /* opt-in: compact single select。 候補を segmented control として常時表示。 */
  .compact-select {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    width: 100%;
    min-width: 0;
  }
  .compact-option {
    font: inherit;
    font-size: 0.8rem;
    line-height: 1.3;
    padding: 2px 10px;
    border: 1px solid var(--border, #d1d5db);
    border-radius: 9999px;
    background: #fff;
    color: var(--muted, #6b7280);
    cursor: pointer;
    white-space: nowrap;
  }
  .compact-option:hover:not(:disabled),
  .compact-option:focus-visible {
    border-color: var(--primary, #2563eb);
    background: #f5f8ff;
    outline: none;
  }
  .compact-option.selected {
    border-color: var(--primary, #2563eb);
    background: var(--primary, #2563eb);
    color: #fff;
    font-weight: 700;
  }
  .compact-option:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  /* `未設定` (クリア) chip。 通常候補と区別できるよう破線・控えめにする。 */
  .compact-option-empty {
    border-style: dashed;
  }
  .compact-option-empty.selected {
    background: var(--muted, #6b7280);
    border-color: var(--muted, #6b7280);
  }
  .inline-control {
    width: 100%;
    box-sizing: border-box;
    font: inherit;
    padding: 2px 6px;
    border: 1px solid var(--primary, #2563eb);
    border-radius: 4px;
    background: #fff;
  }
  .inline-control:focus {
    outline: 2px solid var(--primary, #2563eb);
    outline-offset: -1px;
  }
  .inline-control-textarea {
    resize: vertical;
    font-family: inherit;
    line-height: 1.4;
    min-height: 80px;
  }
  .muted {
    color: var(--muted, #6b7280);
  }
  .small {
    font-size: 11px;
  }
  .saved {
    color: var(--ok, #10b981);
  }
  .error-msg {
    color: var(--danger, #dc2626);
  }
  .status-note {
    flex: 0 0 auto;
  }
  .saving {
    opacity: 0.85;
  }
  .inline-field.locked {
    opacity: 0.7;
  }
  .inline-field.disabled .inline-display {
    color: var(--muted, #6b7280);
    cursor: not-allowed;
  }
  .lock-mark {
    font-size: 11px;
    color: var(--muted, #6b7280);
    flex: 0 0 auto;
  }
</style>
